<template>
  <div>
    <div v-if="errorStatus" class="alert alert-danger my-3" role="alert" v-html="errorStatus">
    </div>
    <div v-if="!isContainerInited" class="d-flex align-items-center justify-content-center" style="min-height:300px">
      <div class="spinner-border mx-2" style="width: 3rem; height: 3rem;" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        Loading...
    </div>
    <div v-else-if="!hasSession" class="alert alert-primary my-3" role="alert">
      <i class="bi bi-lock"></i>You aren't signed into an account. Please <a href="#"  onclick="window.dialogManager?.showSignIn(); return false;">sign in</a> to usage information.
    </div>
    <div v-else-if="isLoading" class="d-flex align-items-center justify-content-center" style="min-height:300px">
        <div class="spinner-border mx-2" style="width: 3rem; height: 3rem;" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        Loading...
    </div>
    <div v-else-if="noData">
      <div class="alert alert-info my-3" role="alert">
        No usage data available.
      </div>
    </div>
    <template v-else>

    <!-- Admin user search -->
    <div v-if="isAdmin" class="card border-0 shadow-sm mb-4 p-3">
      <div class="d-flex align-items-center mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-shield-lock me-2 text-primary" viewBox="0 0 16 16">
          <path d="M5.338 1.59a61 61 0 0 0-2.837.856.48.48 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.8 11.8 0 0 1-2.517 2.453 7 7 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7 7 0 0 1-1.048-.625 11.8 11.8 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 63 63 0 0 1 5.072.56"/>
          <path d="M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415"/>
        </svg>
        <strong>Admin: Look up user usage</strong>
      </div>
      <div class="input-group input-group-sm">
        <input type="text" class="form-control" v-model="adminSearchCriteria" 
          placeholder="Enter username or API key" 
          @keyup.enter="adminFindUser()" :disabled="isAdminSearching" />
        <button class="btn btn-outline-primary" @click="adminFindUser()" :disabled="isAdminSearching || !adminSearchCriteria">
          <span v-if="isAdminSearching" class="spinner-border spinner-border-sm me-1"></span>
          Search
        </button>
        <button v-if="adminSelectedUser" class="btn btn-outline-secondary" @click="adminClearSearch()">
          Back to My Usage
        </button>
      </div>
      <div v-if="adminSearchError" class="text-danger mt-2 small">{{adminSearchError}}</div>
      
      <!-- Search results dropdown -->
      <div v-if="adminSearchResults && !adminSelectedUser" class="mt-2">
        <div v-if="!adminSearchResults.results || !adminSearchResults.results.length" class="text-muted small">No users found.</div>
        <div v-else class="list-group list-group-flush">
          <a v-for="result in adminSearchResults.results" :key="result.groupId" href="#"
            class="list-group-item list-group-item-action d-flex align-items-center py-2 px-3"
            @click.prevent="adminSelectUser(result)">
            <span class="me-2">{{result.username}}</span>
            <span v-if="result.isMasterGroup" class="badge bg-warning text-dark me-1">Group</span>
            <span v-if="result.isAdmin" class="badge bg-primary me-1">Admin</span>
            <span v-if="result.matchType==='child'" class="badge bg-info me-1">Child</span>
          </a>
        </div>
        <div v-if="adminSearchResults.truncated" class="text-muted small mt-1">Results truncated. Try a more specific search.</div>
      </div>
      
      <!-- Selected user banner -->
      <div v-if="adminSelectedUser" class="alert alert-info mt-2 mb-0 py-2 d-flex align-items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-person-fill me-2" viewBox="0 0 16 16">
          <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6"/>
        </svg>
        Viewing usage for: <strong class="ms-1">{{adminSelectedUser}}</strong>
      </div>
    </div>

    <!-- Account / Group name -->
    <div v-if="adminSelectedUser && usageInfo && (usageInfo.groupName || usageInfo.userName)" class="mb-2 text-muted" style="font-size:0.95em;">
      Account: <strong>{{ usageInfo.groupName || usageInfo.userName }}</strong>
    </div>

    <h4>Query Count and Record Cost Summary</h4>
    <div class="text-muted mb-3" style="font-size: 0.95em;">
      All usage is calculated based on UTC time. In your local time, the day starts at: 
      {{ localDayStart }}
    </div>
       <div class="row" v-if="isFairUse || isFixedLimit">             
          <div class="kpi col-12 col-md-6 col-lg-4 pt-3">
            <div class="card border-0 shadow  text-center p-3">
              <div class="h4 text-muted">Queries Today</div>
              <div class="h3">{{queriesText}}</div>
              <div v-if="maxDailyQueriesText" class="fs-s text-muted">{{queriesText}} of {{maxDailyQueriesText}}/day</div>
            </div>
          </div>
          <div class="kpi col-12 col-md-6 col-lg-4 pt-3">
            <div class="card border-0 shadow text-center p-3">
              <div class="h4 text-muted">Record Cost Today</div>
              <div class="h3">{{costText}}</div>
              <div v-if="maxDailyCostText" class="fs-s text-muted">{{costText}} of {{maxDailyCostText}}/day</div> 
            </div>
          </div>
          <div class="kpi col-12 col-md-6 col-lg-4 pt-3">
            <div class="card border-0 shadow text-center p-3">
              <div class="h4 text-muted">Failures Today</div>
              <div class="h3">{{failuresTodayText}}</div>
              <div class="fs-s text-muted">&nbsp;</div>
            </div>
          </div>
          <div class="kpi col-12 col-md-6 col-lg-4 pt-3">
            <div class="card border-0 shadow  text-center p-3">
              <div class="h4 text-muted">Queries This Period</div>
              <div class="h3">{{periodQueriesText}}</div>
              <div v-if="maxMonthlyQueriesText" class="fs-s text-muted">{{periodQueriesText}} of {{maxMonthlyQueriesText}}/month</div> 
            </div>
          </div>
          <div class="kpi col-12 col-md-6 col-lg-4 pt-3">
            <div class="card border-0 shadow text-center p-3">
              <div class="h4 text-muted">Record Cost This Period</div>
              <div class="h3">{{periodCostText}}</div>
              <div v-if="maxMonthlyCostText" class="fs-s text-muted">{{periodCostText}} of {{maxMonthlyCostText}}/month</div>
            </div>
          </div>
          <div v-if="currentPeriodUsage && currentPeriodUsage.period" class="kpi col-12 col-md-6 col-lg-4 pt-3">
            <div class="card border-0 shadow text-center p-3">
              <div class="h4 text-muted">Period Start</div>
              <div class="h3">{{periodStartText}}</div>
              <div class="fs-s text-muted">&nbsp;</div>
            </div>
          </div>
        </div>
        <template v-if="isMetered">
          <div class="row">
            <div class="kpi col-12 col-md-4  pt-3">
              <div class="card border-0 shadow  text-center p-3">
                <div class="h4 text-muted">Queries</div>
                <div class="h3">{{queriesText}}</div>
                <div class="fs-s text-muted">&nbsp;</div>
              </div>
            </div>
            <div class="kpi col-12 col-md-4 pt-3">
              <div class="card border-0 shadow text-center p-3">
                <div class="h4 text-muted">Record Cost</div>
                <div class="h3">{{costText}}</div>
                 <div class="fs-s text-muted">{{freeDailyCostText}} free/day</div>
              </div>
            </div>
            <div class="kpi col-12 col-md-4 pt-3">
              <div class="card border-0 shadow  text-center p-3">
                <div class="h4 text-muted">Available Free Credits</div>
                <div class="h3">{{availableCreditsText}}</div>
                 <div class="fs-s text-muted">&nbsp;</div>
              </div>
            </div>
          </div>
          <h4 class="mt-3">Usage charges</h4>
          <div class="row justify-content-center">
            <div class="kpi col-12 col-md-6">
              <div class="card border-0 shadow  text-center p-3">
                <div class="h4 text-muted">This invoice period</div>
                <div class="h3">{{invoiceBalanceText}}</div>
                <div class="fs-s text-muted">Invoice Date: {{invoiceDueDate}}</div>
               </div>
            </div>
          </div>
        </template>

    <!-- Child Usage Breakdown -->
    <template v-if="hasChildUsage">
      <h4 class="mt-5">User Breakdown (Today)</h4>
      <div class="text-muted mb-2" style="font-size: 0.9em;">Usage by individual users in this account group.</div>
      <table class="table table-striped fs-s">
        <thead>
          <tr>
            <th>User</th>
            <th class="text-end">Queries Today</th>
            <th class="text-end">Cost Today</th>
            <th class="text-end">Failures</th>
            <th class="text-end">Period Queries</th>
            <th class="text-end">Period Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="child in sortedChildUsage" :key="child.userId" 
            :class="{'table-primary': selectedChildUser === child.userId}">
            <td class="text-nowrap px-2 py-1">{{child.userName || child.userId}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(child.queryCount)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(child.cost)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(child.failures || 0)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(child.periodQueryCount || 0)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(child.periodQueryCost || 0)}}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="fw-bold">
            <td class="text-nowrap px-2 py-1">Total</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(childUsageTotals.queryCount)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(childUsageTotals.cost)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(childUsageTotals.failures)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(childUsageTotals.periodQueryCount)}}</td>
            <td class="text-nowrap px-2 py-1 text-end">{{formatNumber(childUsageTotals.periodQueryCost)}}</td>
          </tr>
        </tfoot>
      </table>
    </template>

    <!-- Usage Chart -->
    <div class="d-flex justify-content-between align-items-center mt-5 mb-2">
      <h4 class="mb-0">Usage Over Time</h4>
      <select v-if="hasChildUsage" v-model="selectedChildUser" class="form-select form-select-sm" style="max-width:280px;" @change="onChildUserChanged">
        <option value="">All Users</option>
        <option v-for="child in sortedChildUsage" :key="child.userId" :value="child.userId">
          {{ child.userName || child.userId }}
        </option>
      </select>
    </div>
    <div v-if="isChartLoading" class="text-center py-3"><span class="spinner-border spinner-border-sm me-1"></span> Loading chart data...</div>
    <div class="card border-0 shadow-sm p-3 mb-4">
      <svg ref="usageChart" style="width:100%;height:280px;"></svg>
    </div>

    <h4 class="mt-3">Recent Daily History</h4>
    <table class="table table-striped fs-s">
      <thead>
        <tr>
          <th>Date</th>
          <th class="text-end">Queries</th>
          <th class="text-end">Cost</th>
          <th class="text-end">Failures</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(item,index) in historicalUsageReverse">
          <tr>
            <td scope="col" class="text-nowrap px-2 py-1">{{item.period}} </td>
            <td scope="col" class="text-nowrap px-2 py-1 text-end">{{formatNumber(item.queryCount)}} </td>
            <td scope="col" class="text-nowrap px-2 py-1 text-end">{{formatNumber(item.cost)}} </td>
            <td scope="col" class="text-nowrap px-2 py-1 text-end">{{formatNumber(item.failures || 0)}} </td>
          </tr>
        </template>
      </tbody>
        
    </table>

    </template>
    <CommonModals />
  </div>
 
</template>

<script>
import CommonModals from '/src/components/CommonModals.vue'
import "/src/visualcrossing-core/vccommunicator_account";
import "/src/visualcrossing-core/vccommunicator_serveradmin";
import {VcPrettyNumberFormatter} from '/src/visualcrossing-core/vcprettynumberformatter';
import {VcDateUtils} from "/src/visualcrossing-core/vcdateutils";
import {VcGlobal} from '/src/visualcrossing-core/vcglobal_conn';
import * as d3 from 'd3';
export default {
  name: 'usagesummary',
  props: {
    
  },
  data() {
    return {
      isContainerInited:false,
      sessionId:null,
      sessionState:null,
      errorStatus:null,
      usageInfo:null,
      isLoading:false,
      isChartLoading:false,
      selectedChildUser:'',
      adminSearchCriteria:'',
      adminSearchResults:null,
      adminSelectedUser:null,
      adminSearchError:null,
      isAdminSearching:false,
    }
  },
  methods:{
    formatNumber(value) {
      if (value===null || value===undefined) return '0';
      return VcPrettyNumberFormatter.format(value);
    },
    refreshUsageInfo() {
      var me=this;
      if (!me.hasSession) return;
      me.isLoading=true;
      me.customerInfo=null;
       
      vcCommunicator.usagesummary(true, true, true, true, me.selectedChildUser || null)
        .then(function (result) {
          me.isLoading=false;
         
          me.usageInfo=result;
          me.errorStatus=null;
          me.$nextTick(function() { me.renderUsageChart(); });
        }).catch((error) => {
          me.isLoading=false;
          
          me.errorStatus="There was a problem loading the usage information. <br/>"+error;
        });
    },
    onChildUserChanged() {
      var me=this;
      if (!me.hasSession) return;
      me.isChartLoading=true;
      
      var promise;
      if (me.adminSelectedUser) {
        promise=vcCommunicator.getUserUsageSummary(me.adminSelectedUser, me.selectedChildUser || null);
      } else {
        promise=vcCommunicator.usagesummary(false, true, false, false, me.selectedChildUser || null);
      }
      promise
        .then(function(result) {
          me.isChartLoading=false;
          if (result && result.historicalUsage) {
            me.usageInfo.historicalUsage = result.historicalUsage;
          }
          me.$nextTick(function() { me.renderUsageChart(); });
        }).catch(function(error) {
          me.isChartLoading=false;
          console.error('Error loading filtered usage:', error);
        });
    },
    renderUsageChart() {
      var me=this;
      var svgEl=me.$refs.usageChart;
      if (!svgEl || !me.historicalUsage || me.historicalUsage.length < 2) return;

      var svg=d3.select(svgEl);
      svg.selectAll('*').remove();

      var rect=svgEl.getBoundingClientRect();
      var w=rect.width || 600;
      var h=rect.height || 280;
      var margin={top:20, right:55, bottom:35, left:55};
      var iw=w - margin.left - margin.right;
      var ih=h - margin.top - margin.bottom;

      var data=me.historicalUsage.map(function(d) {
        return { date:new Date(d.period+'T00:00:00'), queries:d.queryCount||0, cost:d.cost||0, failures:d.failures||0 };
      });

      var g=svg.append('g').attr('transform','translate('+margin.left+','+margin.top+')');

      var x=d3.scaleTime().domain(d3.extent(data,function(d){return d.date;})).range([0,iw]);
      var maxQ=d3.max(data,function(d){return d.queries;})||1;
      var maxC=d3.max(data,function(d){return Math.max(d.cost,d.failures);})||1;
      var yQ=d3.scaleLinear().domain([0,maxQ]).nice().range([ih,0]);
      var yC=d3.scaleLinear().domain([0,maxC]).nice().range([ih,0]);

      // Grid
      g.append('g').call(d3.axisLeft(yQ).ticks(5).tickSize(-iw).tickFormat('')).selectAll('line').attr('stroke','#e0e0e0').attr('stroke-dasharray','2,2');
      g.selectAll('.domain').remove();

      // X axis
      g.append('g').attr('transform','translate(0,'+ih+')').call(d3.axisBottom(x).ticks(7).tickFormat(d3.timeFormat('%b %d'))).selectAll('text').style('font-size','11px');

      // Y left (queries)
      g.append('g').call(d3.axisLeft(yQ).ticks(5)).selectAll('text').style('font-size','11px');
      g.append('text').attr('transform','rotate(-90)').attr('y',-42).attr('x',-ih/2).attr('text-anchor','middle').style('font-size','11px').style('fill','#3b82f6').text('Queries');

      // Y right (cost/failures)
      g.append('g').attr('transform','translate('+iw+',0)').call(d3.axisRight(yC).ticks(5)).selectAll('text').style('font-size','11px');
      g.append('text').attr('transform','rotate(90)').attr('y',-(iw+42)).attr('x',ih/2).attr('text-anchor','middle').style('font-size','11px').style('fill','#10b981').text('Cost / Failures');

      // Queries area
      g.append('path').datum(data).attr('d',d3.area().x(function(d){return x(d.date);}).y0(ih).y1(function(d){return yQ(d.queries);}).curve(d3.curveMonotoneX)).attr('fill','rgba(59,130,246,0.12)');

      // Queries line
      g.append('path').datum(data).attr('d',d3.line().x(function(d){return x(d.date);}).y(function(d){return yQ(d.queries);}).curve(d3.curveMonotoneX)).attr('fill','none').attr('stroke','#3b82f6').attr('stroke-width',2);

      // Cost line
      g.append('path').datum(data).attr('d',d3.line().x(function(d){return x(d.date);}).y(function(d){return yC(d.cost);}).curve(d3.curveMonotoneX)).attr('fill','none').attr('stroke','#10b981').attr('stroke-width',2);

      // Failures line
      g.append('path').datum(data).attr('d',d3.line().x(function(d){return x(d.date);}).y(function(d){return yC(d.failures);}).curve(d3.curveMonotoneX)).attr('fill','none').attr('stroke','#ef4444').attr('stroke-width',1.5).attr('stroke-dasharray','4,3');

      // Legend
      var legend=g.append('g').attr('transform','translate('+(iw-220)+', -8)');
      var items=[{label:'Queries',color:'#3b82f6',dash:null},{label:'Cost',color:'#10b981',dash:null},{label:'Failures',color:'#ef4444',dash:'4,3'}];
      items.forEach(function(item,i) {
        var lg=legend.append('g').attr('transform','translate('+(i*78)+',0)');
        lg.append('line').attr('x1',0).attr('y1',5).attr('x2',16).attr('y2',5).attr('stroke',item.color).attr('stroke-width',2).attr('stroke-dasharray',item.dash);
        lg.append('text').attr('x',20).attr('y',9).style('font-size','11px').style('fill','#666').text(item.label);
      });

      // Tooltip overlay
      var focus=g.append('g').style('display','none');
      focus.append('line').attr('class','hover-line').attr('y1',0).attr('y2',ih).attr('stroke','#999').attr('stroke-width',1).attr('stroke-dasharray','3,3');
      var tipBox=focus.append('g');
      tipBox.append('rect').attr('rx',4).attr('ry',4).attr('fill','rgba(0,0,0,0.8)').attr('width',140).attr('height',68);
      var tipText=tipBox.append('text').attr('fill','#fff').style('font-size','11px').attr('x',8).attr('y',16);
      var tipLine1=tipText.append('tspan').attr('x',8).attr('dy',0);
      var tipLine2=tipText.append('tspan').attr('x',8).attr('dy',14);
      var tipLine3=tipText.append('tspan').attr('x',8).attr('dy',14);
      var tipLine4=tipText.append('tspan').attr('x',8).attr('dy',14);

      var bisect=d3.bisector(function(d){return d.date;}).left;
      g.append('rect').attr('fill','none').attr('pointer-events','all').attr('width',iw).attr('height',ih)
        .on('mouseover',function(){focus.style('display',null);})
        .on('mouseout',function(){focus.style('display','none');})
        .on('mousemove',function(event) {
          var x0=x.invert(d3.pointer(event)[0]);
          var i=bisect(data,x0,1);
          var d0=data[i-1], d1=data[i];
          if (!d0 || !d1) return;
          var d=(x0-d0.date > d1.date-x0)?d1:d0;
          focus.select('.hover-line').attr('x1',x(d.date)).attr('x2',x(d.date));
          var tx=x(d.date)+8;
          if (tx+144>iw) tx=x(d.date)-148;
          tipBox.attr('transform','translate('+tx+',4)');
          tipLine1.text(d3.timeFormat('%b %d, %Y')(d.date));
          tipLine2.text('Queries: '+VcPrettyNumberFormatter.format(d.queries));
          tipLine3.text('Cost: '+VcPrettyNumberFormatter.format(d.cost));
          tipLine4.text('Failures: '+VcPrettyNumberFormatter.format(d.failures));
        });
    },
    adminFindUser() {
      var me=this;
      if (!me.adminSearchCriteria) return;
      me.isAdminSearching=true;
      me.adminSearchError=null;
      me.adminSearchResults=null;
      me.adminSelectedUser=null;
      
      vcCommunicator.findUser(me.adminSearchCriteria)
        .then(function(result) {
          me.isAdminSearching=false;
          me.adminSearchResults=result;
        }).catch(function(error) {
          me.isAdminSearching=false;
          me.adminSearchError='Search failed: '+error;
        });
    },
    adminSelectUser(result) {
      var me=this;
      me.adminSelectedUser=result.username;
      me.adminSearchResults=null;
      me.selectedChildUser='';
      me.isLoading=true;
      me.errorStatus=null;
      
      vcCommunicator.getUserUsageSummary(result.username)
        .then(function(usageData) {
          me.isLoading=false;
          me.usageInfo=usageData;
          me.$nextTick(function() { me.renderUsageChart(); });
        }).catch(function(error) {
          me.isLoading=false;
          me.errorStatus='Failed to load usage for user: '+error;
        });
    },
    adminClearSearch() {
      var me=this;
      me.adminSelectedUser=null;
      me.adminSearchResults=null;
      me.adminSearchCriteria='';
      me.adminSearchError=null;
      me.selectedChildUser='';
      me.refreshUsageInfo();
    },
  },
  computed:{
    isAdmin() {
      return this.sessionState && this.sessionState.contextProperties && 
        this.sessionState.contextProperties.isAdmin==="true";
    },
    localDayStart() {
      const now = new Date();
      const utcMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0));
      return utcMidnight.toLocaleTimeString([], { hour: 'numeric', hour12: true }).toLowerCase();
    },
    subscriptionParams() {
      return this.sessionState.subscriptionParams;
    },
    maxDailyCost() {
      var me=this;
      if (me.isFairUse || !me.subscriptionParams || !me.subscriptionParams.maxDailyCost || me.subscriptionParams.maxDailyCost<=0 || me.subscriptionParams.maxDailyCost===2147483647) return -1;
      return me.subscriptionParams.maxDailyCost;
    },
    maxMonthlyCost() {
      var me=this;
      if (me.isFairUse || !me.subscriptionParams || !me.subscriptionParams.maxMonthlyCost || me.subscriptionParams.maxMonthlyCost<=0  || me.subscriptionParams.maxMonthlyCost===2147483647) return -1;
      return me.subscriptionParams.maxMonthlyCost;
    },
    maxDailyQueries() {
      var me=this;
      if (me.isFairUse || !me.subscriptionParams || !me.subscriptionParams.maxQueries || me.subscriptionParams.maxQueries<=0 || me.subscriptionParams.maxQueries===2147483647) return -1;
      return me.subscriptionParams.maxQueries;
    },
    maxMonthlyQueries() {
      var me=this;
      if (me.isFairUse || !me.subscriptionParams || !me.subscriptionParams.maxMonthlyQueries || me.subscriptionParams.maxMonthlyQueries<=0|| me.subscriptionParams.maxMonthlyQueries===2147483647) return -1;
      return me.subscriptionParams.maxMonthlyQueries;
    },
    isFairUse() {
      var me=this;
      return me.subscriptionParams && me.subscriptionParams.fairUsageMaxDailyCost>0;
    },
    isFixedLimit() {
      return !this.isFairUse && !this.isMetered;
    },
    isMetered() {
      return !this.isFairUse && this.freeDailyCost>0;
    },
    freeDailyCost:function() {
      return this.subscriptionParams && this.subscriptionParams.freeDailyCost;
    },
    isMeteredNoPaymentSrc:function() {
      return this.subscriptionParams && this.subscriptionParams.isMeteredNoPaymentSrc===1;
    },
    maxDailyQueriesText() {
      if (!this.maxDailyQueries || this.maxDailyQueries==-1) return null
      return VcPrettyNumberFormatter.format(this.maxDailyQueries);
    },
    maxMonthlyQueriesText() {
      if (!this.maxMonthlyQueries || this.maxMonthlyQueries==-1) return null
      return VcPrettyNumberFormatter.format(this.maxMonthlyQueries);
    },
    maxDailyCostText() {
      var me=this;
      if (me.maxDailyCost>0) return VcPrettyNumberFormatter.format(me.maxDailyCost);
      return null;
    },
    maxMonthlyCostText() {
       var me=this;
       if (me.maxMonthlyCost>0) return VcPrettyNumberFormatter.format(me.maxMonthlyCost);
         return null;
    },
    freeDailyCostText() {
      return VcPrettyNumberFormatter.format(this.freeDailyCost);
    },
    failuresTodayText() {
      if (!this.currentPeriodUsage) return '0';
      return VcPrettyNumberFormatter.format(this.currentPeriodUsage.failures || 0);
    },
    costPercentText:function() {
      var me=this;
      var cost=me.currentPeriodUsage.cost;
      var maxCost=me.freeDailyCost;
      if (!maxCost) maxCost=me.maxDailyCost;
      var used=100*cost/maxCost;
      if (used>100) used=100;
      if (used<1) return "<1%";
      return Math.round(used)+"%";
    },
    queriesText() {
      return VcPrettyNumberFormatter.format(this.currentPeriodUsage.queryCount);
    },
    periodQueriesText() {
      return VcPrettyNumberFormatter.format(this.currentPeriodUsage.periodQueryCount);
    },
    costText() {
      return VcPrettyNumberFormatter.format(this.currentPeriodUsage.cost);
    },
    periodCostText() {
      return VcPrettyNumberFormatter.format(this.currentPeriodUsage.periodQueryCost);
    },
    periodStartText() {
      if (!this.currentPeriodUsage || !this.currentPeriodUsage.period) return '-';
      try {
        var date = new Date(this.currentPeriodUsage.period);
        if (isNaN(date.getTime())) return this.currentPeriodUsage.period;
        return VcDateUtils.formatDate(date);
      } catch(e) {
        return this.currentPeriodUsage.period;
      }
    },
    availableCreditsText() {
      return VcPrettyNumberFormatter.format(this.currentPeriodUsage.availableCredits);
    },
    currentPeriodUsage() {
      return this.usageInfo && this.usageInfo.currentPeriodUsage;
    },
    historicalUsage() {
      return this.usageInfo && this.usageInfo.historicalUsage;
    },
    historicalUsageReverse() {
        if (!this.historicalUsage) return null;
        return [...this.historicalUsage].reverse();
    },
    childUsage() {
      return this.usageInfo && this.usageInfo.childUsage;
    },
    hasChildUsage() {
      return this.childUsage && this.childUsage.length>0;
    },
    sortedChildUsage() {
      if (!this.childUsage) return [];
      return [...this.childUsage].sort((a,b) => (b.queryCount + b.cost) - (a.queryCount + a.cost));
    },
    childUsageTotals() {
      var children=this.sortedChildUsage;
      var t={queryCount:0, cost:0, failures:0, periodQueryCount:0, periodQueryCost:0};
      for (var i=0;i<children.length;i++) {
        t.queryCount+=(children[i].queryCount||0);
        t.cost+=(children[i].cost||0);
        t.failures+=(children[i].failures||0);
        t.periodQueryCount+=(children[i].periodQueryCount||0);
        t.periodQueryCost+=(children[i].periodQueryCost||0);
      }
      return t;
    },
    noData() {
      return !this.usageInfo;
    },
    hasSession() {
      return this.sessionId;
    },
    upcomingInvoice:function() {
      var me=this;
      if (!me.usageInfo || !me.usageInfo.customerInfo || !me.usageInfo.customerInfo.subscriptions || !me.usageInfo.customerInfo.subscriptions.length) return null;
      return me.usageInfo.customerInfo.subscriptions[0].upcomingInvoice;
    },
    invoiceBalance:function() {
      var me=this;
      if (!me.upcomingInvoice) return 0;
      return  (me.upcomingInvoice.amount/100);
    },
    invoiceBalanceText:function() {
      var me=this;
      if (!me.invoiceBalance) return "-";
      return "$"+(me.invoiceBalance);
    },
    invoiceDueDate:function() {
      var me=this;

      var date=me.upcomingInvoice && new Date(me.upcomingInvoice.invoiceDate)
      if (!date) return "-";
      return VcDateUtils.formatDate(date);

    },
  },
  updated() {
   
  },
  unmounted() {
    var me=this;
    if (me._resizeHandler) window.removeEventListener('resize', me._resizeHandler);
  },
  mounted() {
    var me=this;
   
    var vcCommunicator=window.vcCommunicator;
    if (!vcCommunicator) {
      console.log("No vcCommunicator");
      return;
    }
    me.sessionId=vcCommunicator.sessionId;
    me.sessionState=vcCommunicator.sessionState;
    me.isContainerInited=vcCommunicator.isInited || me.sessionId;
    vcCommunicator.addHandler('createsession', function (sessionId,sessionState) {
      me.sessionId=sessionId;
      me.sessionState=sessionState;
      me.isContainerInited=true;
      me.refreshUsageInfo();
    });
     vcCommunicator.addHandler('terminatesession', function () {
       me.sessionId=null;
    });

    
    me._resizeHandler=function() {
      if (me._resizeTimer) clearTimeout(me._resizeTimer);
      me._resizeTimer=setTimeout(function() { me.renderUsageChart(); }, 200);
    };
    window.addEventListener('resize', me._resizeHandler);

    me.refreshUsageInfo();
  },
  components: {
    CommonModals
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .kpi .card {
    height:150px
  }
</style>